import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Button, IconButton, Skeleton, Typography } from '@mui/material';
import React, { useState } from 'react';

import {
  ALERT_CONTENT,
  CLAIM_LINK_TEMPLATE_CARD_MAX_WIDTH,
  LEGACY_CLAIM_PAGE_SETTINGS_ALERT,
} from '../../../../constants/templates';
import { ClaimLinkTemplateSourceEnum, IClaimLinkTemplate } from '../../../../types/templates';
import { ClaimLinkTemplateCardBase } from '../../../TemplatesComponent';
import { ClaimLinkTypeInfoAlert } from './ClaimLinkTypeInfoAlert';

interface IProps {
  title: string;
  subtitle?: string;
  selectedTemplate: IClaimLinkTemplate | null;
  onSelect: (event: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  actions?: React.ReactNode;
  preselectionSource?: ClaimLinkTemplateSourceEnum | null;
  isCampaignDefault?: boolean;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  showLegacySettingsAlert?: boolean;
}

export const ClaimLinkTemplateSelector: React.FC<IProps> = ({
  title,
  subtitle,
  selectedTemplate,
  onSelect,
  disabled,
  loading,
  actions,
  preselectionSource,
  isCampaignDefault,
  emptyStateTitle = 'No template selected',
  emptyStateDescription,
  showLegacySettingsAlert,
}) => {
  const { name, cover_image_url, is_default, updated_at } = selectedTemplate ?? {};

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const alertContent = preselectionSource ? ALERT_CONTENT[preselectionSource] : null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsAlertOpen(false);
      setIsClosing(false);
    }, 250);
  };

  return (
    <Box sx={{ mb: 6 }}>
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.4 }}>
          <Typography fontWeight="medium" sx={{ fontSize: 18 }}>
            {title}
          </Typography>
          {preselectionSource && !isAlertOpen && (
            <IconButton
              sx={{ width: 22, height: 22 }}
              onMouseEnter={() => setIsAlertOpen(true)}
              data-action="template_info_btn"
            >
              <InfoOutlinedIcon sx={{ width: 20, height: 20 }} />
            </IconButton>
          )}
        </Box>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            width: CLAIM_LINK_TEMPLATE_CARD_MAX_WIDTH,
            flex: '0 0 auto',
          }}
        >
          {loading ? (
            <Skeleton variant="rounded" width="100%" height={240} />
          ) : (
            <ClaimLinkTemplateCardBase
              title={name ?? emptyStateTitle}
              thumbnailUrl={cover_image_url}
              isDefault={is_default ?? false}
              isCampaignDefault={isCampaignDefault}
              isHighlighted={false}
              updatedAt={selectedTemplate ? updated_at : undefined}
              description={!selectedTemplate ? emptyStateDescription : undefined}
            />
          )}
          {actions ?? (
            <Button variant="outlined" color="primary" onClick={onSelect} disabled={disabled || loading} fullWidth>
              {selectedTemplate ? 'Change template' : 'Choose template'}
            </Button>
          )}
        </Box>
        {alertContent && (
          <ClaimLinkTypeInfoAlert
            title={alertContent.title}
            message={alertContent.message}
            isOpen={isAlertOpen}
            isClosing={isClosing}
            onClose={handleClose}
          />
        )}
      </Box>
      {showLegacySettingsAlert && (
        <Alert severity="warning" sx={{ mt: 1.5 }} data-action="legacy_campaign_settings_alert">
          {LEGACY_CLAIM_PAGE_SETTINGS_ALERT}
        </Alert>
      )}
    </Box>
  );
};
